/**
 * The "Use Entire Data Catalog" control — one component, every chat surface.
 *
 * It exists because the markup was copy-pasted per surface, and a surface that
 * did not carry its own copy simply had no button: an empty board rendered
 * `BoardEntryCardV2`'s agent picker, which never had one, so the toggle vanished
 * exactly when a user starting a fresh board would reach for it. Fixing that by
 * pasting the block a third time would have set the next surface up to lose it
 * the same way.
 *
 * Presentational on purpose — state stays with the caller. The two stores behind
 * this flag (`useAppStore.entireDataCatalog` and `useChatV2Store`'s field of the
 * same name) are not yet merged, and a component that reached into one of them
 * could not be used by the surfaces backed by the other. Consolidating the state
 * is a separate step; making the CONTROL single is what stops surfaces from
 * silently losing it, so it lands first.
 *
 * Design: docs/catalog-scope-root-cause-and-design.md §3.2.
 */
import { Check, Database } from 'lucide-react'

import { useTranslation } from '@/i18n'

interface CatalogScopeToggleProps {
  checked: boolean
  onChange: (next: boolean) => void
  /** Rendered inside a popover on every current caller; kept overridable for the next one. */
  className?: string
}

export function CatalogScopeToggle({ checked, onChange, className }: CatalogScopeToggleProps) {
  const { t } = useTranslation(['chatv2'])

  return (
    <button
      type="button"
      aria-pressed={checked}
      data-testid="entire-catalog-toggle"
      onClick={() => onChange(!checked)}
      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-colors hover:bg-accent/50 ${
        checked ? 'text-blue-500 dark:text-blue-400 font-medium' : 'text-foreground'
      } ${className ?? ''}`}
    >
      {/* Presentational checkbox — decorative (aria-hidden); the button conveys
          the toggle state via aria-pressed. Mirrors the visual of
          @/components/ui/checkbox without nesting an interactive control inside
          the button. */}
      <span
        aria-hidden="true"
        data-testid="entire-catalog-checkbox"
        data-state={checked ? 'checked' : 'unchecked'}
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border bg-background ${
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
        }`}
      >
        {checked && <Check className="h-3.5 w-3.5" />}
      </span>
      <Database
        className={`w-4 h-4 flex-shrink-0 ${
          checked ? 'text-blue-500 dark:text-blue-400' : 'text-muted-foreground'
        }`}
      />
      <span className="flex-1 text-left truncate">{t('chatv2:input.entire_data_catalog')}</span>
    </button>
  )
}

export default CatalogScopeToggle
